import type { FindCondition, Product, ProductCategory, ProductMetadata } from '@/types';
import { productPublishInstant, showsSoldOut } from '@/lib/catalog/availability';

/** Logical path of the «Hallazgos» page (locale prefix added by localizedPath / the router). */
export const FINDS_PATH = '/shop/hallazgos';

/** The only store whose one-off pieces are finds. */
export const FINDS_CATEGORY: ProductCategory = 'estilo';

/**
 * One definition of a «hallazgo» (client page, prerender, JSON-LD): a one-off
 * piece (`metadata.uniquePiece`, «Única» in /admin) of Estilo, the fashion and
 * accessories Carolina finds in shops, travels and sales. Casa also marks
 * pieces «Única» (NAOR teddies) and those are not finds, hence the category.
 * No new field: a sold find stays listed (at the end, «Pieza vendida»). Pure.
 */
export function isFind(p: Pick<Product, 'category' | 'metadata'>): boolean {
  return p.category === FINDS_CATEGORY && p.metadata.uniquePiece === true;
}

/** The finds of a listing, in the listing's own order (storeListing / api.products). */
export function findsOf<T extends Pick<Product, 'category' | 'metadata'>>(list: readonly T[]): T[] {
  return list.filter(isFind);
}

/** The values of `metadata.findCondition`, best first (labels: dictionary `finds.condition.<value>`). */
export const FIND_CONDITIONS = ['new_with_tags', 'new', 'excellent', 'good'] as const satisfies readonly FindCondition[];

/**
 * schema.org `itemCondition` of a product's offer: a find with a condition
 * maps new_with_tags / new → NewCondition and excellent / good → UsedCondition;
 * everything else keeps NewCondition (CARO's regular stock is new). Pure.
 */
export function offerItemCondition(p: Pick<Product, 'category' | 'metadata'>): string {
  const c = isFind(p) ? p.metadata.findCondition : undefined;
  return c === 'excellent' || c === 'good'
    ? 'https://schema.org/UsedCondition'
    : 'https://schema.org/NewCondition';
}

/**
 * The finds in drop order for /shop/hallazgos (FindsPage and the prerender):
 * the pieces still on sale first and the sold ones last (as listingOrder), each
 * group newest drop first. A piece's drop = its `publishAt`, or its `createdAt`
 * when it was never scheduled; same drop (or no usable date) keeps the incoming
 * listing order (stable sort). Pure.
 */
export function findsInDropOrder<T extends Product>(finds: readonly T[]): T[] {
  const dropOf = (p: T): number => {
    const at = productPublishInstant(p) ?? Date.parse(p.createdAt);
    return Number.isFinite(at) ? at : -Infinity;
  };
  const byDrop = (a: T, b: T): number => {
    const x = dropOf(a);
    const y = dropOf(b);
    return x === y ? 0 : x > y ? -1 : 1;
  };
  const onSale = finds.filter((p) => !showsSoldOut(p)).sort(byDrop);
  const sold = finds.filter(showsSoldOut).sort(byDrop);
  return [...onSale, ...sold];
}

/**
 * Metadata keys the Hallazgos tab owns (and their translatable i18n subkeys).
 * The Catálogo CSV neither exports nor overwrites them: see
 * `withoutFindMetadata` (export, preview diff) and `keepFindMetadata` (import).
 */
const FIND_META_KEYS = ['findStory', 'findMeasurements', 'findCondition', 'publishAt'] as const;
const FIND_I18N_KEYS = ['findStory', 'findMeasurements'] as const;

/** `meta` without the find keys (top level and per locale); a locale left empty is dropped. Pure. */
export function withoutFindMetadata(meta: ProductMetadata): ProductMetadata {
  const out: ProductMetadata = { ...meta };
  for (const k of FIND_META_KEYS) delete out[k];
  if (out.i18n) {
    const i18n: NonNullable<ProductMetadata['i18n']> = {};
    for (const [locale, tr] of Object.entries(out.i18n)) {
      const t = { ...tr };
      for (const k of FIND_I18N_KEYS) delete t[k];
      if (Object.keys(t).length) i18n[locale] = t;
    }
    if (Object.keys(i18n).length) out.i18n = i18n;
    else delete out.i18n;
  }
  return out;
}

/**
 * The metadata a CSV row writes: `incoming` (the sheet) with the find keys
 * taken from `existing` (the stored row) instead, so an import never sets,
 * changes nor wipes a find's story / measurements / condition / publishAt,
 * even from an old export. Pure.
 */
export function keepFindMetadata(existing: ProductMetadata, incoming: ProductMetadata): ProductMetadata {
  const out = withoutFindMetadata(incoming);
  for (const k of FIND_META_KEYS) {
    if (existing[k] !== undefined) (out as Record<string, unknown>)[k] = existing[k];
  }
  for (const [locale, tr] of Object.entries(existing.i18n ?? {})) {
    const kept = Object.fromEntries(FIND_I18N_KEYS.flatMap((k) => (tr[k] !== undefined ? [[k, tr[k]]] : [])));
    if (Object.keys(kept).length === 0) continue;
    out.i18n = { ...(out.i18n ?? {}), [locale]: { ...(out.i18n?.[locale] ?? {}), ...kept } };
  }
  return out;
}

/**
 * The find blocks of a product page, already translated (ProductDetailPage and
 * the Worker prerender `productHtml` read this one shape, so they show the same
 * text): «La historia» and the specs «Medidas» / «Estado». «Estado» only for a
 * used piece (excellent / good): new_with_tags / new is not shown (owner rule
 * 2026-10-15; JSON-LD itemCondition still says New). `p` is the
 * LOCALIZED product (localizeProduct overlays i18n.findStory / findMeasurements).
 * null for a non-find or a find with none of the three fields. Pure.
 */
export interface FindDetails {
  story?: { title: string; text: string };
  specs: { label: string; value: string }[];
}
/** Conditions the product page does not show (a new piece needs no «Estado» line). */
const FIND_CONDITIONS_HIDDEN: ReadonlySet<string> = new Set(['new_with_tags', 'new']);

export function findDetails(
  p: Pick<Product, 'category' | 'metadata'>,
  t: (key: string) => string,
): FindDetails | null {
  if (!isFind(p)) return null;
  const story = p.metadata.findStory?.trim();
  const measurements = p.metadata.findMeasurements?.trim();
  const condition = p.metadata.findCondition;
  const specs: FindDetails['specs'] = [];
  if (measurements) specs.push({ label: t('finds.measurements'), value: measurements });
  if (condition && (FIND_CONDITIONS as readonly string[]).includes(condition) && !FIND_CONDITIONS_HIDDEN.has(condition)) {
    specs.push({ label: t('finds.condition'), value: t(`finds.condition.${condition}`) });
  }
  if (!story && specs.length === 0) return null;
  return { ...(story ? { story: { title: t('finds.story'), text: story } } : {}), specs };
}
